/**
 * components/team/OverridesModal.js
 * Per-member GRANT / DENY exceptions on top of a role. Shared by the organizer
 * and vendor team pages so the precedence rule (DENY > GRANT > role default)
 * is explained in exactly one place.
 */
import React, { useState } from "react";
import { S } from "./teamStyles.js";
import { Alert } from "./TeamPrimitives.js";
import { AppButton, AppDialog } from "../ui/index.js";

const EXCEPTION_BUTTON = {
  GRANT: { variant: "outline", icon: "fa fa-plus", label: "Grant" },
  DENY: { variant: "danger", icon: "fa fa-ban", label: "Deny" },
};

/**
 * Three states per key so the precedence rule is visible rather than implied:
 * inherit from the role, GRANT on top, or DENY over the top. DENY always
 * wins, and the label says so.
 */
const OverridesModal = ({ member, roleLabel, permissions, rolePermissions, grantableKeys, onClose, onSubmit, busy, error }) => {
  const [map, setMap] = useState(() => {
    const m = new Map();
    for (const o of member.customPermissions || []) m.set(o.key, o.type);
    return m;
  });

  const cycle = (key) =>
    setMap((prev) => {
      const next = new Map(prev);
      const current = next.get(key);
      if (!current) next.set(key, "GRANT");
      else if (current === "GRANT") next.set(key, "DENY");
      else next.delete(key);
      return next;
    });

  const roleSet = new Set(rolePermissions);
  const effective = (key) => {
    const o = map.get(key);
    if (o === "DENY") return false;
    if (o === "GRANT") return true;
    return roleSet.has(key);
  };

  return (
    <AppDialog
      open
      size="lg"
      title={`Exceptions for ${member.name || member.email || member.userId}`}
      subtitle={`Role: ${roleLabel || member.roleKey}. Exceptions add to or take away from what the role allows.`}
      busy={busy}
      onClose={onClose}
      footer={
        <>
          <AppButton variant="quiet" onClick={onClose} disabled={busy}>Cancel</AppButton>
          <AppButton loading={busy} onClick={() => onSubmit([...map.entries()].map(([key, type]) => ({ key, type })))}>
            Save exceptions
          </AppButton>
        </>
      }
    >
      <div className="lk-note" style={{ marginBottom: 14 }}>
        <i className="fa fa-info-circle" aria-hidden="true" style={{ marginTop: 2 }} />
        <span>
          Click a permission's button to move it through Inherit, Grant and Deny. Deny always wins over
          Grant, and Grant wins over the role. You can only grant permissions you hold yourself.
        </span>
      </div>
      <Alert type="error" msg={error} />
      <div style={S.tableWrap}>
        <table style={{ ...S.table, minWidth: 640 }}>
          <thead>
            <tr>
              <th style={S.th}>Permission</th>
              <th style={S.th}>Role allows</th>
              <th style={S.th}>Exception</th>
              <th style={S.th}>Result</th>
            </tr>
          </thead>
          <tbody>
            {permissions.map((p) => {
              const o = map.get(p.key);
              const canGrant = grantableKeys.has(p.key);
              const look = EXCEPTION_BUTTON[o] || { variant: "quiet", icon: null, label: "Inherit" };
              const allowed = effective(p.key);
              return (
                <tr key={p.key}>
                  <td style={S.td}>
                    <div style={{ fontWeight: 600 }}>{p.label}</div>
                    <div style={{ fontSize: 11, color: "#64748b" }}>{p.description}</div>
                  </td>
                  <td style={S.td}>{roleSet.has(p.key) ? "Yes" : "No"}</td>
                  <td style={S.td}>
                    <AppButton
                      size="sm"
                      variant={look.variant}
                      icon={look.icon}
                      disabled={!canGrant && o !== "GRANT"}
                      title={!canGrant && o !== "GRANT" ? "You do not hold this permission, so you cannot grant it" : "Click to change"}
                      onClick={() => cycle(p.key)}
                      style={{ minWidth: 84, justifyContent: "center" }}
                    >
                      {look.label}
                    </AppButton>
                  </td>
                  <td style={S.td}>
                    <span className="lk-pill" style={{ background: allowed ? "#dcfce7" : "#fee2e2", color: allowed ? "#047857" : "#b91c1c" }}>
                      {allowed ? "Allowed" : "Not allowed"}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </AppDialog>
  );
};

export default OverridesModal;
